import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AD_TYPES,
  AD_STATES,
  TARGET_TYPES,
  WALMART_TOTAL_BUDGET_DEFAULT,
  WALMART_SELLER_SINGLE_CAMPAIGN_MIN_BUDGET,
  WALMART_SELLER_DOUBLE_CAMPAIGN_MIN_BUDGET,
  WALMART_VENDOR_SINGLE_CAMPAIGN_MIN_BUDGET,
  WALMART_VENDOR_DOUBLE_CAMPAIGN_MIN_BUDGET,
  SD_CAMPAIGN_TYPE_OPTIONS,
  SB_BID_STRATEGY_OPTIONS,
  BID_STRATEGY_OPTIONS,
  GOALS,
  SB_CAMPAIGN_GOAL_OPTIONS,
  ALGORITHM_OPTIONS,
  MIN_SB_TARGET_CPC,
  SB_BRAND_AD_TYPE,
} from "./constants";
import { AdPlanContext } from "./AdPlanContext";
import { useFetch } from "../../../../hooks/api";
import { useGetStorePageProducts } from "./useGetStorePageProducts";
import { useAsinsByProductId } from "./useAsinsByProductId";
import { campaignNamesToCheck, useCampaignNameChecks } from "./useCampaignNameChecks";
import { formatCurrency } from "../../../../utils/formatNumber";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { acosToRoas, getCreativeAssetId } from "./utils";
import { useMountedState } from "@reactuses/core";
import { getExponentialBackoff } from "../../../../utils/backoff";
import { getFirstKeywordError, useKeywordErrorMessage } from "../../../../modules/advertising/utils/keywordValidation";

const EMPTY_ARRAY = [];

const useAdPlanState = (
  initialAdPlan = {},
  marketPlace,
  categoryId = null,
  adId = null,
  roas,
  organization_type,
  isArchivedAd = false,
) => {
  initialAdPlan = {
    name: "",
    canHaveChildren: false,
    hasParent: false,
    componentTemplate: undefined,
    children: [],
    categoryId,
    targetACOS: roas ? 5 : 20,
    maxACOS: roas ? 2.5 : 40,
    goal: undefined,
    dailyBudget: marketPlace === "IN" ? 1500 : 20,
    totalBudget: WALMART_TOTAL_BUDGET_DEFAULT,
    filledTags: [],
    filledNegativeKeywords: [],
    filledAsins: [],
    filledNegativeAsins: [],
    filledTargetCategories: [],
    filledTargetAudiences: [],
    filledProductKeys: [],
    filledProductBrands: [],
    filledCompetitorBrands: [],
    filteredAttributes: {
      color: [],
      size: [],
      collection: [],
      flavor: [],
      material: [],
      other: [],
    },
    product: [],
    landingPageProduct: [],
    storePageProduct: [],
    creativeProduct: [],
    excludedProducts: [],
    adType: null,
    type: "single",
    advStatus: AD_STATES.ACTIVE.value,
    brand: "",
    creativeBrandName: "",
    store: "",
    page: "",
    customSdStorePageEnabled: false,
    logo: "",
    channel: localStorage.channel === "amazon" ? "AMZ" : localStorage.channel,
    allocation: 30,
    headline: "",
    newLandingPage: false,
    newProductCollection: false,
    manualCollection: false,
    autoCollection: false,
    discoverKeywords: true,
    scheduledActions: [],
    inventoryFilterBrand: "",
    targetType: TARGET_TYPES.MANUAL.value,
    minBid: null,
    maxBid: null,
    disableBidding: false,
    asinHarvestingEnabled: false,
    keywordHarvestingEnabled: false,
    keywordDiscoveryEnabled: false,
    autoCampaignEnabled: false,
    topOfSearchBidMultiplier: 50,
    productPageMultiplier: 0,
    restOfSearchMultiplier: 0,
    amazonBusinessMultiplier: 0,
    audienceBidMultiplier: 0,
    audienceBidMultiplierTargetAudienceId: null,
    audienceBidMultiplierTargetAudienceName: null,
    audienceBidMultiplierTargetAudienceSegmentType: null,
    bidStrategy: 0,
    manualCampaignBudget: 70,
    autoCampaignBudget: 30,
    renderAdvancedOptions: undefined,
    notifications: {
      maxAcos: 30,
      maxSpend: 10,
    },
    policyViolations: [],
    // Sponsored Display V2 Fields
    sdBidType: null,
    sdCampaignType: null,
    lookbackPeriod: [],
    viewsSimilar: false,
    viewsExact: false,
    purchasesSimilar: false,
    purchasesExact: false,
    sdDynamicSimilar: false,
    salesVolumeControl: 2,
    // New SB Multipliers
    homePlacementBidMultiplier: 0,
    pagePlacementBidMultiplier: 0,
    otherPlacementBidMultiplier: 0,
    shopperSegmentBidMultiplier: 0,
    topOfSearchPlacementBidMultiplier: 0,
    amazonSBBidOptimization: false,
    sbCampaignGoal: SB_CAMPAIGN_GOAL_OPTIONS.PAGE_VISIT.value,
    costControlTargetCPCEnabled: false,
    costControlTargetCPC: null,
    // Negation Settings
    autoNegationEnabled: false,
    maxAcosNegationThreshold: null,
    cumulativeNegationThreshold: null,
    // Walmart Options - TODO: Consider breaking the growing list of ad control fields into channel-specific objects, as well as a generic object of shared ad control params
    // Placement Bid Multipliers
    buyBoxPlacementBidMultiplier: 0,
    searchInGridPlacementBidMultiplier: 0,
    stockUpPlacementBidMultiplier: 0,
    homePagePlacementBidMultiplier: 0,
    // Platform Bid Multipliers
    desktopPlatformBidMultiplier: 0,
    mobilePlatformBidMultiplier: 0,
    appPlatformBidMultiplier: 0,
    // Placement Toggles
    searchCarouselCampaignPlacement: true,
    itemCarouselCampaignPlacement: true,
    buyBoxCarouselCampaignPlacement: true,
    walmartSuggestedKeywordsEnabled: false,
    // Walmart SB Creative Fields
    walmartBrandName: "",
    walmartHeadline: "",
    walmartClickURL: "",
    walmartImage: "",
    // Walmart options end
    campaignNames: {},
    campaignIds: {},
    manualHarvestingEnabled: false,
    harvestingClicks: 0,
    harvestingConversions: 3,
    harvestingDays: 10,
    harvestingViewImpressions: 0,
    acosLookbackWindow: null,
    crLookbackWindow: 180,
    biddingProgram: null,
    tags: [],
    algoManagedPlacementMultipliers: false,
    ignoreSmartSpendACOSRange: false,
    siteRestrictions: null,
    // The plan exactly as the server last returned it, or null for a plan that has never been saved.
    // Anything that has to distinguish "what the user typed" from "what the server holds" reads this
    // rather than tracking its own copy: it is rebuilt from the payload on every refetch, so it can
    // never drift from the server the way a locally accumulated snapshot would.
    savedAdPlan: null,
    ...initialAdPlan,
  };

  const { t } = useTranslation(["advertising"]);
  const keywordErrorMessage = useKeywordErrorMessage();
  const [adPlan, setAdPlan] = useState(initialAdPlan);
  const campaignNameChecks = useCampaignNameChecks(campaignNamesToCheck(adPlan), marketPlace);
  const [storesData, setStoresData] = useState([]);
  const [logoOptions, setLogoOptions] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [agreeToArchive, setAgreeToArchive] = useState(false);

  const { setSavedChildPlans } = useContext(AdPlanContext);
  const hasAMCModule = useModuleEnabled("AMC");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const isMounted = useMountedState();

  const onChange = useCallback((partialAdPlan) => {
    setAdPlan((prevAdPlan) => ({
      ...prevAdPlan,
      ...partialAdPlan,
    }));
  }, []);

  const storePageProductsQuery = useGetStorePageProducts(adPlan.page?.value, {
    enabled:
      !!adPlan.page?.value &&
      [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(adPlan.adType),
  });

  const { data: brandData, isLoading: isBrandDataLoading } = useFetch(
    ["brands", marketPlace],
    "/brands/",
    { marketplace: marketPlace },
    {
      select: (res) => res.data,
      staleTime: 60 * 60 * 1000,
      retry: 3,
      retryDelay: getExponentialBackoff,
    },
  );

  const brandOptions = useMemo(() => {
    if (!brandData) {
      return [];
    }
    return (
      brandData?.map((b) => ({
        value: b.brand_entity_id,
        label: b.brand_registry_name,
      })) ?? []
    );
  }, [brandData]);

  const adPlanConfigQuery = useFetch(
    ["adPlanConfig", adId, categoryId, isArchivedAd, isMounted()],
    "/gvads/advproduct",
    {
      advertisement: adId,
      ...(isArchivedAd ? { archived: true } : {}),
    },
    {
      enabled: !!adId,
    },
  );

  // Products saved on the plan carry their own asin, so they resolve without consulting the catalog —
  // and they stay resolvable even when the plan's products fall outside the currently scoped category.
  const savedPlanProducts = adPlanConfigQuery.data?.data?.products ?? EMPTY_ARRAY;

  // The duplicate-asin check is the one thing that needs an asin before the plan is saved, and it only
  // looks at creative products — at most 10 — so that is all this resolves. Everything else sends
  // product ids and lets the server resolve them.
  const creativeProductAsins = useAsinsByProductId(adPlan.creativeProduct);

  const asinsByProductId = useMemo(() => {
    const byId = new Map(creativeProductAsins);
    // The plan's own products win: they stay correct even once a product leaves the scoped category.
    for (const p of savedPlanProducts) {
      byId.set(String(p.id), p.asin);
    }
    return byId;
  }, [creativeProductAsins, savedPlanProducts]);

  // An asin can map to several product ids (the same listing sold MFN / FBA / Flex), so this is
  // deliberately one-to-many. Scoped to the plan's own products, which is what callers resolving a
  // saved creative's asins back to selections actually mean.
  const productIdsByAsin = useMemo(() => {
    const byAsin = new Map();
    for (const p of savedPlanProducts) {
      if (!p.asin) {
        continue;
      }
      const ids = byAsin.get(p.asin);
      if (ids) {
        ids.push(p.id);
      } else {
        byAsin.set(p.asin, [p.id]);
      }
    }
    return byAsin;
  }, [savedPlanProducts]);

  useEffect(() => {
    if (!adPlanConfigQuery.data?.data) {
      return;
    }
    const adPlanData = {
      data: adPlanConfigQuery.data?.data,
    };

    const asinsToIds = (asins) => {
      return (asins ?? []).map((asin) => productIdsByAsin.get(asin)?.[0]).filter((p) => p);
    };

    const brand = brandOptions?.find((b) => b.value === adPlanData.data.brand_entity);

    const useNewCreativeFormat = Object.values(SB_BRAND_AD_TYPE).includes(adPlanData.data.creative?.brand_ad_type);

    if (
      [
        SB_BRAND_AD_TYPE.PRODUCT_COLLECTION,
        SB_BRAND_AD_TYPE.MANUAL_COLLECTION,
        SB_BRAND_AD_TYPE.AUTO_COLLECTION,
      ].includes(adPlanData.data.creative?.brand_ad_type)
    ) {
      adPlanData.data.ad_type = AD_TYPES.SPONSORED_BRAND.value;
    }

    if ([SB_BRAND_AD_TYPE.BRAND_VIDEO, SB_BRAND_AD_TYPE.VIDEO].includes(adPlanData.data.creative?.brand_ad_type)) {
      adPlanData.data.ad_type = AD_TYPES.SPONSORED_BRAND_VIDEO.value;
    }

    const formatCategoryTargetData = (targetCategories) => {
      const formattedTargetCategoryData = targetCategories?.map((targetDetails) => {
        if (typeof targetDetails === "number") {
          targetDetails = { id: targetDetails, savedToServer: true };

          return targetDetails;
        }

        if (targetDetails.asin_price_between) {
          targetDetails = {
            asin_price_greater_than: targetDetails.asin_price_between.split("-")?.[0],
            asin_price_less_than: targetDetails.asin_price_between?.split("-")?.[1],
            ...targetDetails,
          };
          delete targetDetails.asin_price_between;
        }

        if (targetDetails.asin_review_rating_between) {
          targetDetails = {
            asin_review_rating_greater_than: targetDetails.asin_review_rating_between?.split("-")?.[0],
            asin_review_rating_less_than: targetDetails.asin_review_rating_between?.split("-")?.[1],
            ...targetDetails,
          };
          delete targetDetails.asin_review_between;
        }

        if (targetDetails.brand === "string") {
          targetDetails.brand = {
            label: targetDetails.brand_name,
            value: targetDetails.brand,
          };
        }

        targetDetails.savedToServer = true;

        return targetDetails;
      });

      return formattedTargetCategoryData;
    };

    const adPlan = {
      categoryId,
      name: adPlanData.data.name,
      product: adPlanData.data.products?.map((p) => p.id) ?? [],
      adType: adPlanData.data.ad_type,
      goal:
        adPlanData.data.is_composite && adPlanData.data.ad_goal !== GOALS.IMPORTED_ADS.value ?
          GOALS.CUSTOM.value
        : adPlanData.data.ad_goal,
      targetACOS: roas ? acosToRoas(adPlanData.data.target_acos) : adPlanData.data.target_acos,
      maxACOS: roas ? acosToRoas(adPlanData.data.max_acos) : adPlanData.data.max_acos,
      targetAcosFromServer: adPlanData.data.target_acos,
      maxAcosFromServer: adPlanData.data.max_acos,
      dailyBudget: adPlanData.data.daily_budget,
      totalBudget: adPlanData.data.total_budget,
      advStatus: adPlanData.data.state,
      // TODO: Below is confusing, we also have moderation status being stored, but the UI for SB checks
      // for ad status and state combo, but in the line above and throughout UI, what is ad_state in API is referred to as ad_status
      advModerationStatus: adPlanData.data.ad_status,
      servingStatus: adPlanData.data.serving_status,
      creativeModerationStatus:
        (
          typeof adPlanData.data?.moderation_status === "string" &&
          adPlanData.data?.ad_type === AD_TYPES.SPONSORED_DISPLAY_V2.value
        ) ?
          adPlanData.data?.moderation_status
        : null,
      channel: adPlanData.data.channel,
      filledTags: adPlanData.data.themed_kw_list ?? [],
      filledAsins: adPlanData.data.themed_asin_list ?? [],
      filledNegativeAsins: adPlanData.data.negative_asin_list ?? [],
      filledNegativeKeywords: adPlanData.data.negative_kw_list,
      filledTargetCategories: formatCategoryTargetData(adPlanData.data.user_category_id_list),
      filledTargetAudiences:
        adPlanData.data.custom_audience_id_list?.map((savedAudience) => ({
          label: savedAudience.name,
          value: savedAudience.id,
        })) ?? [],
      allocation: adPlanData.data.budget_allocation,
      canHaveChildren: adPlanData.data.is_composite,
      headline:
        useNewCreativeFormat ?
          adPlanData.data.creative?.properties?.[0]?.creative?.headline
        : (adPlanData.data.creative?.headline ?? adPlanData.data.creative?.properties?.headline ?? ""),
      scheduledActions: adPlanData.data?.scheduled_actions?.filter((action) => action.status !== "cancelled"),
      type: adPlanData.data.type,
      children:
        adPlanData.data.children?.map((c) => ({
          id: c.id,
          name: c.name,
          allocation: c.budget_allocation,
          advStatus: c.ad_status,
          targetACOS: roas ? acosToRoas(c.target_acos) : c.target_acos,
          maxACOS: roas ? acosToRoas(c.max_acos) : c.max_acos,
          targetAcosFromServer: c.target_acos,
          maxAcosFromServer: c.max_acos,
          adType: c.ad_type,
        })) ?? [],
      id: adPlanData.data.id,
      parent: adPlanData.data.parent,
      hasParent: !!adPlanData.data.parent,
      video:
        useNewCreativeFormat ?
          adPlanData.data.creative?.properties?.[0]?.creative.videoAssetIds?.[0]
        : (adPlanData.data.video_id ?? adPlanData.data.creative?.videoAssetIds?.[0]),
      brand,
      creativeBrandName:
        useNewCreativeFormat ? adPlanData.data.creative?.properties?.[0]?.creative?.brandName : brand?.label,
      logo:
        useNewCreativeFormat ?
          adPlanData.data.creative?.properties?.[0]?.creative?.brandLogoAssetID
        : (adPlanData.data.creative?.brandLogoAssetID ??
          adPlanData.data.creative?.properties?.brandLogo?.assetId ??
          null),
      creativeProduct:
        useNewCreativeFormat ?
          asinsToIds(adPlanData.data.creative?.properties?.[0]?.creative?.asins)
        : asinsToIds(adPlanData.data.creative?.asins ?? []),
      landingPageProduct:
        useNewCreativeFormat ?
          asinsToIds(adPlanData.data.creative?.properties?.[0]?.landingPage?.asins)
        : asinsToIds(adPlanData.data.landing_page?.asins ?? []),
      storePageProduct:
        useNewCreativeFormat ?
          asinsToIds(adPlanData.data.creative?.properties?.[0]?.creative?.asins)
        : asinsToIds(adPlanData.data.creative?.asins ?? []),
      newLandingPage:
        useNewCreativeFormat ?
          !!adPlanData.data.creative?.properties?.[0]?.landingPage?.asins?.length
        : !!adPlanData.data.landing_page?.asins?.length,
      storeLandingPage:
        useNewCreativeFormat ?
          !!adPlanData.data.creative?.properties?.[0]?.landingPage?.url
        : !!adPlanData.data.landing_page?.url,

      newProductCollection: false,
      manualCollection: false,
      autoCollection: false,
      excludedProducts: [],
      nonTrellisEnrolledAd: adPlanData.data.non_trellis_enrolled_ad,
      nonTrellisEnrolledAdAcceptBids: adPlanData.data.non_trellis_enrolled_ad_accept_bids,
      nonTrellisEnrolledAdAcceptBidsStartDate: adPlanData.data.non_trellis_enrolled_ad_accept_bids_start_date,
      filledProductKeys: [],
      filledProductBrands: [],
      filledCompetitorBrands: [],
      inventoryFilterBrand: "",
      filteredAttributes: {
        color: [],
        size: [],
        collection: [],
        flavor: [],
        material: [],
        other: [],
      },
      targetType: adPlanData.data?.targeting_type,
      minBid: adPlanData.data?.ad_control_fields?.min_bid ?? null,
      maxBid: adPlanData.data?.ad_control_fields?.max_bid ?? null,
      disableBidding: adPlanData.data?.ad_control_fields?.disable_bidding ?? null,
      asinHarvestingEnabled: adPlanData.data?.ad_control_fields?.asin_harvesting ?? false,
      crSelectorEnabled: adPlanData.data?.ad_control_fields?.cr_selector ?? false,
      keywordHarvestingEnabled: adPlanData.data?.ad_control_fields?.keyword_harvesting ?? false,
      autoCampaignEnabled: adPlanData.data?.ad_control_fields?.with_auto ?? false,
      autoCampaignEnabledFromServer: adPlanData.data?.ad_control_fields?.with_auto ?? false,
      keywordDiscoveryEnabled: adPlanData.data?.discover_keywords ?? false,
      trellisTrakEnabled: adPlanData.data?.ad_control_fields?.historical_keywords ?? false,
      algorithm: adPlanData.data?.ad_control_fields?.bid_algo,
      manualCampaignBudget: adPlanData.data?.ad_control_fields?.discovery_budget_alloc * 100,
      autoCampaignBudget: adPlanData.data?.ad_control_fields?.auto_budget_alloc * 100,
      bidStrategy: adPlanData.data?.ad_control_fields?.bid_strategy ?? BID_STRATEGY_OPTIONS.DOWN_ONLY.value,
      autoNegationEnabled: adPlanData.data?.ad_control_fields?.auto_negation ?? false,
      maxAcosNegationThreshold: adPlanData.data?.ad_control_fields?.target_acos_negation_threshold * 100,
      cumulativeNegationThreshold: adPlanData.data?.ad_control_fields?.cumm_threshold * 100,
      notifications: {
        maxAcos: adPlanData.data?.notification_settings?.max_acos ?? 30,
        maxSpend: adPlanData.data?.notification_settings?.max_spend ?? 10,
        productAlertEnabled: adPlanData.data?.notification_settings?.product_alert ?? false,
      },
      salesVolumeControl: adPlanData.data?.ad_control_fields?.sales_volume_control_slider ?? 2,
      broadMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("broad"),
      initialBroadMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("broad"),
      phraseMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("phrase"),
      initialPhraseMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("phrase"),
      exactMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("exact"),
      initialExactMatchEnabled: adPlanData.data?.ad_control_fields?.match_types?.includes("exact"),
      negativePhraseMatchEnabled: adPlanData.data?.ad_control_fields?.negative_match_types?.includes("phrase"),
      initialNegativePhraseMatchEnabled: adPlanData.data?.ad_control_fields?.negative_match_types?.includes("phrase"),
      negativeExactMatchEnabled: adPlanData.data?.ad_control_fields?.negative_match_types?.includes("exact"),
      initialNegativeExactMatchEnabled: adPlanData.data?.ad_control_fields?.negative_match_types?.includes("exact"),
      broadMatchRegular: adPlanData.data?.ad_control_fields?.broad_match_regular,
      initialBroadMatchRegular: adPlanData.data?.ad_control_fields?.broad_match_regular,
      broadMatchModifier: adPlanData.data?.ad_control_fields?.broad_match_modifier,
      initialBroadMatchModifier: adPlanData.data?.ad_control_fields?.broad_match_modifier,
      weekPartingMethod: adPlanData.data?.ad_control_fields?.week_parting_method,
      weekPartingMultipliers: adPlanData.data?.ad_control_fields?.week_parting_multipliers,
      dayPartingMethod: adPlanData.data?.ad_control_fields?.day_parting_method,
      dayPartingMultipliers: adPlanData.data?.ad_control_fields?.day_parting_multipliers,
      policyViolations: adPlanData.data?.policy_violations ?? [],
      // Process stored values for existing plans => will need to test when get view updated
      sdCampaignType: adPlanData.data?.ad_control_fields?.campaign_type,
      lookbackPeriod:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value ?
          adPlanData.data?.ad_control_fields?.lookback
        : [],
      sdBidType: adPlanData.data?.ad_control_fields?.cost_type,
      viewsSimilar:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
        adPlanData.data?.ad_control_fields?.targets?.includes("views_similar"),
      viewsExact:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
        adPlanData.data?.ad_control_fields?.targets?.includes("views_exact"),
      purchasesSimilar:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
        adPlanData.data?.ad_control_fields?.targets?.includes("purchases_similar"),
      purchasesExact:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
        adPlanData.data?.ad_control_fields?.targets?.includes("purchases_exact"),
      sdDynamicSimilar:
        adPlanData.data?.ad_control_fields?.campaign_type === SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL.value &&
        adPlanData.data?.ad_control_fields?.targets?.includes("contextual_dynamic"),
      sdAmazonSuggestedCategories: !!(
        adPlanData.data?.amz_recommended_catergory || adPlanData.data?.amazon_category_id_list?.length > 0
      ),
      customSdStorePageEnabled:
        adPlanData.data?.ad_type === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
        adPlanData.data?.landing_page?.landingPageType === "STORE",
      creativeAsset: getCreativeAssetId(useNewCreativeFormat, adPlanData),
      sdSquareImageCroppingCoordinates:
        adPlanData.data?.creative?.properties?.squareCustomImage?.croppingCoordinates ?? {},
      sdRectangleImageCroppingCoordinates:
        adPlanData.data?.creative?.properties?.rectCustomImage?.croppingCoordinates ?? {},
      sdRectangleCroppedArea: adPlanData.data?.creative_display_metadata?.rectCustomImage?.croppedArea ?? {},
      sdSquareCroppedArea: adPlanData.data?.creative_display_metadata?.squareCustomImage?.croppedArea ?? {},

      // Walmart sales control
      buyBoxPlacementBidMultiplier: adPlanData.data?.ad_control_fields?.placement_bid_multipliers?.["Buy-Box"] ?? 0,
      searchInGridPlacementBidMultiplier:
        adPlanData.data?.ad_control_fields?.placement_bid_multipliers?.["Search Ingrid"] ?? 0,
      stockUpPlacementBidMultiplier: adPlanData.data?.ad_control_fields?.placement_bid_multipliers?.["Stock Up"] ?? 0,
      homePagePlacementBidMultiplier: adPlanData.data?.ad_control_fields?.placement_bid_multipliers?.["Home Page"] ?? 0,
      // Platform Bid Multipliers
      desktopPlatformBidMultiplier: adPlanData.data?.ad_control_fields?.device_bid_multipliers?.Desktop ?? 0,
      mobilePlatformBidMultiplier: adPlanData.data?.ad_control_fields?.device_bid_multipliers?.Mobile ?? 0,
      appPlatformBidMultiplier: adPlanData.data?.ad_control_fields?.device_bid_multipliers?.App ?? 0,
      // Placement Toggles
      searchCarouselCampaignPlacement: adPlanData.data?.ad_control_fields?.campaign_placements?.["Search Carousel"],
      itemCarouselCampaignPlacement: adPlanData.data?.ad_control_fields?.campaign_placements?.["Item Carousel"],
      buyBoxCarouselCampaignPlacement: adPlanData.data?.ad_control_fields?.campaign_placements?.["Item Buybox"],
      walmartSuggestedKeywordsEnabled: adPlanData.data?.ad_control_fields?.suggested_keywords,
      walmartHeadline: adPlanData.data?.creative?.headline,
      walmartBrandName: adPlanData.data?.creative?.brand_name,
      walmartAssetId: adPlanData.data?.creative?.asset_id,
      walmartCaptionAssetId: adPlanData.data?.creative?.caption_asset_id,
      walmartClickURL: adPlanData.data?.creative?.click_url,
      amazonSBBidOptimization: adPlanData.data?.bid_optimization ?? false,
      shopperSegmentBidMultiplier:
        adPlanData.data?.ad_control_fields?.bid_adjustments_by_shopper_segment?.[0]?.percentage ?? 0,

      campaignNames: {},
      campaignIds: {},
      // New manual harvesting settings
      manualHarvestingEnabled: adPlanData.data?.ad_control_fields?.harvesting_setting_override,
      harvestingClicks: adPlanData.data?.ad_control_fields?.harvesting_clicks,
      harvestingConversions: adPlanData.data?.ad_control_fields?.harvesting_conversions,
      harvestingDays: adPlanData.data?.ad_control_fields?.harvesting_days,
      harvestingViewImpressions: adPlanData.data?.ad_control_fields?.harvesting_view_impressions,
      acosLookbackWindow: adPlanData.data?.ad_control_fields?.acos_lookback_window,
      crLookbackWindow: adPlanData.data?.ad_control_fields?.cr_ctr_lookback_window,
      sbCampaignGoal: adPlanData.data?.ad_control_fields?.campaign_goal ?? SB_CAMPAIGN_GOAL_OPTIONS.PAGE_VISIT.value,
      costControlTargetCPCEnabled: !!(
        adPlanData.data?.ad_control_fields?.sb_cost_control_target_cpc ??
        adPlanData.data?.ad_control_fields?.sd_cost_control_target_cpc
      ),
      costControlTargetCPC:
        adPlanData.data?.ad_control_fields?.sb_cost_control_target_cpc ??
        adPlanData.data?.ad_control_fields?.sd_cost_control_target_cpc ??
        null,
      biddingProgram: adPlanData.data?.ad_control_fields?.program,
      tags:
        tagsEnhancedEnabled ? (adPlanData.data?.tags_enhanced?.map((t) => t.id) ?? []) : (adPlanData.data?.tags ?? []),
      siteRestrictions: adPlanData.data?.ad_control_fields?.site_restrictions ?? null,
      algoManagedPlacementMultipliers:
        adPlanData.data?.ad_control_fields?.placement_multiplier ||
        adPlanData.data?.ad_control_fields?.org_placement_multiplier,
      ignoreSmartSpendACOSRange: adPlanData.data?.ad_control_fields?.disable_acos_range,
    };

    const {
      manual_name,
      auto_name,
      contextual_name,
      audience_name,
      walmart_manual_campaign_name,
      walmart_auto_campaign_name,
      manual_campaign_id,
      auto_campaign_id,
      contextual_campaign_id,
      audience_campaign_id,
      walmart_manual_campaign_id,
      walmart_auto_campaign_id,
    } = adPlanData.data.ad_control_fields;

    if (manual_name) {
      adPlan.campaignNames.manual_name = manual_name;
      adPlan.campaignIds.manual_id = manual_campaign_id;
      adPlan.savedManualCampaignName = manual_name;
    }
    if (auto_name) {
      adPlan.campaignNames.auto_name = auto_name;
      adPlan.campaignIds.auto_id = auto_campaign_id;
      adPlan.savedAutoCampaignName = auto_name;
    }
    if (contextual_name) {
      adPlan.campaignNames.contextual_name = contextual_name;
      adPlan.campaignIds.contextual_id = contextual_campaign_id;
      adPlan.savedContextualCampaignName = contextual_name;
    }
    if (audience_name) {
      adPlan.campaignNames.audience_name = audience_name;
      adPlan.campaignIds.audience_id = audience_campaign_id;
      adPlan.savedAudienceCampaignName = audience_name;
    }

    if (walmart_manual_campaign_name) {
      adPlan.campaignNames.walmart_manual_campaign_name = walmart_manual_campaign_name;
      adPlan.campaignIds.walmart_manual_campaign_id = walmart_manual_campaign_id;
      adPlan.savedWalmartManualCampaignName = walmart_manual_campaign_name;
    }

    if (walmart_auto_campaign_name) {
      adPlan.campaignNames.walmart_auto_campaign_name = walmart_auto_campaign_name;
      adPlan.campaignIds.walmart_auto_campaign_id = walmart_auto_campaign_id;
      adPlan.savedWalmartAutoCampaignName = walmart_auto_campaign_name;
    }

    if (
      [
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ].includes(adPlanData.data.ad_type)
    ) {
      adPlan.homePlacementBidMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find((adjustment) =>
          ["HOME"].includes(adjustment.placement),
        )?.percentage ?? 0;
      adPlan.pagePlacementBidMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find((adjustment) =>
          ["DETAIL_PAGE"].includes(adjustment.placement),
        )?.percentage ?? 0;
      adPlan.otherPlacementBidMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find((adjustment) =>
          ["OTHER"].includes(adjustment.placement),
        )?.percentage ?? 0;
      adPlan.topOfSearchPlacementBidMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find((adjustment) =>
          ["TOP_OF_SEARCH", "PLACEMENT_TOP"].includes(adjustment.placement),
        )?.percentage ?? 0;
    }

    if ([AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adPlanData.data.ad_type)) {
      adPlan.topOfSearchBidMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find(
          (adjustment) =>
            ["PLACEMENT_TOP"].includes(adjustment.placement) || ["placementTop"].includes(adjustment.predicate),
        )?.percentage ?? 0;
      adPlan.productPageMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find(
          (adjustment) =>
            ["PLACEMENT_PRODUCT_PAGE"].includes(adjustment.placement) ||
            ["placementProductPage"].includes(adjustment.predicate),
        )?.percentage ?? 0;
      adPlan.restOfSearchMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find(
          (adjustment) =>
            ["PLACEMENT_REST_OF_SEARCH"].includes(adjustment.placement) ||
            ["placementRestOfSearch"].includes(adjustment.predicate),
        )?.percentage ?? 0;
      adPlan.amazonBusinessMultiplier =
        adPlanData.data.ad_control_fields.bid_adjustments_by_placement?.find((adjustment) =>
          ["SITE_AMAZON_BUSINESS"].includes(adjustment.placement),
        )?.percentage ?? 0;
    }

    if (
      [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ].includes(adPlanData.data.ad_type)
    ) {
      const audienceMultiplierSpec = adPlanData.data.ad_control_fields.shopper_cohort_bidding?.find((adjustment) =>
        ["AUDIENCE_SEGMENT"].includes(adjustment.shopperCohortType),
      );
      if (audienceMultiplierSpec && audienceMultiplierSpec.percentage) {
        adPlan.audienceBidMultiplierTargetAudienceId = audienceMultiplierSpec.audienceSegments?.[0]?.audienceId ?? null;
        adPlan.savedAudienceBidMultiplierTargetAudienceId =
          audienceMultiplierSpec.audienceSegments?.[0]?.audienceId ?? null;
        adPlan.audienceBidMultiplier = audienceMultiplierSpec.percentage ?? 0;
        adPlan.audienceBidMultiplierTargetAudienceName = audienceMultiplierSpec.audienceSegments?.[0]?.name;
        adPlan.audienceBidMultiplierTargetAudienceSegmentTypeFromServer =
          audienceMultiplierSpec.audienceSegments?.[0]?.audienceSegmentType ?? null;
      } else {
        adPlan.audienceBidMultiplierTargetAudienceId = null;
        adPlan.audienceBidMultiplierTargetAudienceSegmentTypeFromServer = null;
        adPlan.audienceBidMultiplier = 0;
      }
    }

    // SB New target options
    if (adPlanData.data?.attributes !== null) {
      adPlan.filledProductKeys = adPlanData.data?.attributes?.product_types;
      adPlan.filledProductBrands = adPlanData.data?.attributes?.brands;
      adPlan.filledCompetitorBrands = adPlanData.data?.attributes?.competitors;
      adPlan.filteredAttributes = adPlanData.data?.attributes?.attributes;
    }

    if (adPlanData.data?.ad_control_fields?.campaign_type === SB_BRAND_AD_TYPE.PRODUCT_COLLECTION) {
      adPlan.adType = AD_TYPES.SPONSORED_BRAND.value;
      adPlan.sbV2 = true;
      adPlan.newProductCollection = true;
    } else if (adPlanData.data?.ad_control_fields?.campaign_type === SB_BRAND_AD_TYPE.MANUAL_COLLECTION) {
      adPlan.adType = AD_TYPES.SPONSORED_BRAND.value;
      adPlan.sbV2 = true;
      adPlan.manualCollection = true;
    } else if (adPlanData.data?.ad_control_fields?.campaign_type === SB_BRAND_AD_TYPE.AUTO_COLLECTION) {
      adPlan.adType = AD_TYPES.SPONSORED_BRAND.value;
      adPlan.sbV2 = true;
      adPlan.autoCollection = true;
      const savedCreative = adPlanData.data.creative?.properties?.[0]?.creative;
      adPlan.excludedProducts = savedCreative?.excludedProductIds ?? asinsToIds(savedCreative?.asinExclusions);
    } else if (adPlanData.data?.ad_control_fields?.campaign_type === SB_BRAND_AD_TYPE.STORE_SPOTLIGHT) {
      adPlan.adType = AD_TYPES.SPONSORED_BRAND.value;
      adPlan.sbV2 = true;
      adPlan.storeSpotlight = true;
      adPlan.storeSpotlightSubpages = adPlanData.data.creative?.properties?.[0]?.creative?.subpages;
    } else if (
      [SB_BRAND_AD_TYPE.BRAND_VIDEO, SB_BRAND_AD_TYPE.VIDEO].includes(adPlanData.data?.ad_control_fields?.campaign_type)
    ) {
      adPlan.adType = AD_TYPES.SPONSORED_BRAND_VIDEO.value;
      adPlan.sbV2 = true;
    }

    if (adPlan.children?.length > 0) {
      const processedChildPlans =
        adPlanData.data.children?.map((c) => ({
          id: c.id,
          name: c.name,
          allocation: c.budget_allocation,
          advStatus: c.ad_status,
          targetACOS: roas ? acosToRoas(c.target_acos) : c.target_acos,
          maxACOS: roas ? acosToRoas(c.max_acos) : c.max_acos,
          targetAcosFromServer: c.target_acos,
          maxAcosFromServer: c.max_acos,

          adType: c.ad_type,
        })) ?? [];
      setSavedChildPlans(processedChildPlans);
    }
    onChange({ ...adPlan, savedAdPlan: adPlan });
  }, [
    adId,
    adPlanConfigQuery.data?.data,
    brandOptions,
    categoryId,
    onChange,
    productIdsByAsin,
    roas,
    setSavedChildPlans,
    tagsEnhancedEnabled,
  ]);

  const savedVideosQuery = useFetch(
    ["savedVideos", marketPlace],
    "/creatives/media/video",
    {
      all: true,
      market_place: marketPlace,
    },
    {
      select: (res) =>
        res.data?.map((v) => ({
          value: v["id"],
          label: v["name"] ? v["name"] : v["project_name"],
        })),
      enabled: false,
    },
  );

  const storeDataQuery = useFetch(
    [adPlan.categoryId, adPlan.brand?.value, adPlan.adType, marketPlace, adPlan.customSdStorePageEnabled],
    "/stores/",
    adPlan.brand?.value ?
      {
        entityid: adPlan.brand.value,
        market_place: marketPlace,
      }
    : {
        pages_only: true,
        market_place: marketPlace,
      },
    {
      enabled:
        !!adPlan.brand?.value ||
        (adPlan.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && !!adPlan.customSdStorePageEnabled),
      staleTime: 60 * 60 * 1000,
      retry: 3,
      retryDelay: getExponentialBackoff,
    },
  );

  useEffect(() => {
    if (!storeDataQuery.data?.data) {
      return;
    }

    const { brand_assets: brandAssetsData, store_pages: storesData } = storeDataQuery.data.data;
    const storeOptions = storesData.map((store, i) => ({
      value: i,
      label: store.store_name,
    }));
    const logoOptions = brandAssetsData?.map((asset) => ({
      value: asset.asset_id,
      label: asset.name,
      url: asset.url,
    }));

    setStoresData(storesData);
    setStoreOptions(storeOptions);
    setLogoOptions(logoOptions);
  }, [storeDataQuery.data?.data]);

  useEffect(() => {
    const sdStoreLandingPageUrl =
      adPlanConfigQuery.data?.data?.ad_type === AD_TYPES.SPONSORED_DISPLAY_V2.value ?
        adPlanConfigQuery.data?.data?.landing_page?.landingPageUrl
      : undefined;
    const hasStoreName = !!adPlanConfigQuery.data?.data?.store_name;
    const hasSdStoreLandingPage = !!sdStoreLandingPageUrl;

    if (!storeOptions?.length || (!hasStoreName && !hasSdStoreLandingPage)) {
      return;
    }
    if (sdStoreLandingPageUrl) {
      const matchingStoreData = storesData.find((sd) => sd.store_pages?.some((p) => p.url === sdStoreLandingPageUrl));
      onChange({
        store: matchingStoreData?.store_name ?? null,
        page: sdStoreLandingPageUrl,
      });
      return;
    }

    const store = storeOptions?.find((s) => s.label === adPlanConfigQuery.data?.data?.store_name) ?? {};
    const page =
      pageOptions.find((p) => p.value === adPlanConfigQuery.data?.data?.landing_page?.url) ??
      pageOptions.find((p) => p.value === adPlanConfigQuery.data?.data?.creative?.properties?.[0]?.landingPage?.url) ??
      {};

    onChange({ store, page });
  }, [
    adId,
    storeOptions,
    storesData,
    pageOptions,
    adPlanConfigQuery.data?.data?.store_name,
    adPlanConfigQuery.data?.data?.landing_page?.url,
    adPlanConfigQuery.data?.data?.creative?.properties,
    adPlanConfigQuery.data?.data?.ad_type,
    adPlanConfigQuery.data?.data?.landing_page?.landingPageUrl,
    onChange,
  ]);

  const fetchStorePagesData = (storeName, storesData) => {
    const pages = storesData.find((s) => s.store_name === storeName)?.store_pages ?? [];
    const formattedPageOptions = pages.map((page) => ({
      value: page.url,
      label: page.name,
    }));
    setPageOptions(formattedPageOptions);
    if (adPlan.storeSpotlight && !adId) {
      const homePage = formattedPageOptions.find((page) => page.label?.startsWith("Home") || page.label === storeName);
      onChange({ page: { value: homePage?.value, label: homePage?.label } });
    }

    return pageOptions;
  };

  const lifestyleAssetDetailsQuery = useFetch(
    ["lifestyleAssetDetails", adPlan.creativeAsset, adPlan.id],
    "/assets/",
    {
      asset_id: adPlan.creativeAsset,
      marketplace: marketPlace,
    },
    {
      select: (res) => res.data?.results?.[0],
      default: [],
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      enabled:
        !!adPlan.creativeAsset &&
        !!adPlan.id &&
        (adPlan.selectedType === "lifestyleImageDetails" || !adPlan.selectedType),
    },
  );

  useEffect(() => {
    if (!lifestyleAssetDetailsQuery.data) {
      return;
    }

    if (lifestyleAssetDetailsQuery.data?.type === "VIDEO") {
      onChange({ videoDetails: lifestyleAssetDetailsQuery.data });
    } else {
      onChange({ lifestyleImageDetails: lifestyleAssetDetailsQuery.data });
    }
  }, [lifestyleAssetDetailsQuery.data, onChange]);

  const logoAssetDetailsQuery = useFetch(
    ["logoAssetDetails", adPlan.logo, adPlan.id],
    "/assets/",
    {
      asset_id: adPlan.logo,
      marketplace: marketPlace,
    },
    {
      select: (res) => res.data?.results?.[0],
      default: [],
      enabled: !!adPlan.logo && !!adPlan.id,
    },
  );

  useEffect(() => {
    if (!logoAssetDetailsQuery.data) {
      return;
    }
    onChange({ logoDetails: logoAssetDetailsQuery.data });
  }, [logoAssetDetailsQuery.data, onChange]);

  useEffect(() => {
    if (adPlan.adType !== AD_TYPES.SPONSORED_DISPLAY_V2.value && adPlan.targetType === "N/A") {
      onChange({ targetType: null });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.adType]);

  useEffect(() => {
    if (adPlan.adType !== AD_TYPES.CUSTOM.value && !adPlan.hasParent) {
      setSavedChildPlans([]);
      if (!adPlan.canHaveChildren) {
        onChange({ children: [] });
      }
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.adType]);

  useEffect(() => {
    // Clear products when category changes - fix issue where switching categories after product selection allows ads with products from multiple categories
    if (!adPlan.id && !adPlan.hasParent && !adPlan.preselectedProducts) {
      setAdPlan({
        ...adPlan,
        product: [],
        landingPageProduct: [],
        creativeProduct: [],
        storePageProduct: [],
      });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.adType, adPlan?.page?.value]);

  useEffect(() => {
    fetchStorePagesData(adPlan.store?.label, storesData);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.store]);

  useEffect(() => {
    if (
      adPlan.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value ||
      adPlan.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
    ) {
      onChange({ targetType: TARGET_TYPES.AUTOMATIC.value });
    }

    if (adPlan.adType === AD_TYPES.SPONSORED_PRODUCT && adPlan.hasParent) {
      onChange({ targetType: TARGET_TYPES.MANUAL.value });
    }

    // Always set SB and SBV plans to have keyword discovery enabled and auto campaign disabled
    if (adPlan.adType === AD_TYPES.SPONSORED_BRAND.value || adPlan.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
      onChange({ keywordHarvestingEnabled: true, autoCampaignEnabled: false });
    } else if (!adPlan.id && (adPlan.newLandingPage || adPlan.storeSpotlight)) {
      onChange({ newLandingPage: false, storeSpotlight: false });
    }

    if (
      (adPlan.adType === AD_TYPES.SPONSORED_BRAND.value && (adPlan.newProductCollection || adPlan.storeSpotlight)) ||
      (adPlan.adType === AD_TYPES.SPONSORED_BRAND.value && adPlan.sbV2) ||
      (adPlan.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !adId)
    ) {
      if (
        ![
          SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
          SB_BID_STRATEGY_OPTIONS.MAXIMIZE_NEW_TO_BRAND_CUSTOMERS.value,
        ].includes(adPlan.bidStrategy) &&
        !adId
      ) {
        onChange({
          bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
        });
      }
      if (adPlan.storeSpotlight && (!adPlan.storeSpotlightSubpages || adPlan.storeSpotlightSubpages?.length < 3)) {
        onChange({ storeSpotlightSubpages: [{}, {}, {}] });
      }

      if (!adPlan.storeSpotlight && adPlan.storeSpotlightSubpages) {
        onChange({ storeSpotlightSubpages: [] });
      }
      // If moving from SB to any other type retroactively, restore defaults
    } else if (
      ![
        BID_STRATEGY_OPTIONS.DOWN_ONLY.value,
        BID_STRATEGY_OPTIONS.UP_DOWN.value,
        BID_STRATEGY_OPTIONS.FIXED.value,
      ].includes(adPlan.bidStrategy) &&
      !adId
    ) {
      if (
        [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(adPlan.adType) &&
        !adPlan.id
      ) {
        return;
      }

      onChange({
        bidStrategy: BID_STRATEGY_OPTIONS.DOWN_ONLY.value,
        storeSpotlight: false,
        storeSpotlightSubpages: [],
      });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adPlan.adType,
    adPlan.newProductCollection,
    adPlan.newLandingPage,
    adPlan.storeLandingPage,
    adPlan.storeSpotlight,
  ]);

  useEffect(() => {
    if ((adPlan.targetType === TARGET_TYPES.FUNNEL.value || adPlan.targetType === TARGET_TYPES.MANUAL.value) && !adId) {
      onChange({ keywordDiscoveryEnabled: false });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.targetType]);

  useEffect(() => {
    if (
      (AD_TYPES.SPONSORED_DISPLAY_V2.value === adPlan.adType && !adPlan.id) ||
      (adPlan.selectedType === "lifestyleImageDetails" && adPlan.id)
    ) {
      onChange({ sdSquareCroppedArea: null, sdRectangleCroppedArea: null });
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.lifestyleImageDetails?.asset_id]);

  useEffect(() => {
    if (adId || adPlan?.storeSpotlight) {
      return;
    }
    const filteredCreativeProducts = adPlan?.creativeProduct?.filter((productId) =>
      adPlan?.landingPageProduct?.includes(productId),
    );

    onChange({ creativeProduct: filteredCreativeProducts });

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlan.landingPageProduct]);

  const validate = () => {
    const errors = {};
    let isValid = true;

    const check = (condition, key, msg) => {
      if (condition) {
        isValid = false;
        errors[key] = msg;
      }
    };

    const planToValidate = adPlan;
    check(
      !planToValidate.hasParent && !planToValidate.categoryId,
      "categoryId",
      t(($) => $.advertising["Category is required"]),
    );
    check(
      planToValidate.name?.length < 1,
      "name",
      t(($) => $.advertising["Name is required"]),
    );
    check(
      !planToValidate.name?.match(
        /^([\]\w&+"Á®ÉÓÜóüÄáŒÖßúäöÛéÀÂâÿŸàèÙçœæÔÆñíÇêëûÈÏéÊïÍîËôÚÎùÑąĄćĆęĘłŁńŃśŚźŹżŻåÅ|()*,/:;=?@_`{~$¿¡«»ĳĲ\s\-\\/-[\u3000-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF00-\uFFEF\s+]*)$/g,
      ) ||
        (!adId && /[<>'!%#./}]/.test(planToValidate.name ?? "")),
      "name",
      t(($) => $.advertising["Invalid name. Name cannot include the following characters: <>'!%#./}"]),
    );
    check(
      !planToValidate.hasParent && planToValidate.dailyBudget === "",
      "dailyBudget",
      t(($) => $.advertising["Daily budget is required"]),
    );
    check(
      planToValidate.channel !== "walmart" && !planToValidate.hasParent && planToValidate.dailyBudget < 1,
      "dailyBudget",

      t(($) => $.advertising["Daily budget must be at least {amount}"], {
        amount: formatCurrency(1, {
          marketPlace: marketPlace ?? "USA",
          decimalPlaces: 0,
        }),
      }),
    );

    if (planToValidate.channel === "walmart") {
      const minBudgetRequirement =
        organization_type === "vendor" ?
          (
            [AD_TYPES.SPONSORED_PRODUCT_WALMART.value, AD_TYPES.SPONSORED_PRODUCT.value].includes(
              planToValidate?.adType,
            ) &&
            planToValidate.goal !== GOALS.SOV.value &&
            planToValidate.autoCampaignEnabled
          ) ?
            WALMART_VENDOR_DOUBLE_CAMPAIGN_MIN_BUDGET
          : WALMART_VENDOR_SINGLE_CAMPAIGN_MIN_BUDGET
        : (
          [AD_TYPES.SPONSORED_PRODUCT_WALMART.value, AD_TYPES.SPONSORED_PRODUCT.value].includes(
            planToValidate?.adType,
          ) &&
          planToValidate.goal !== GOALS.SOV.value &&
          planToValidate.autoCampaignEnabled
        ) ?
          WALMART_SELLER_DOUBLE_CAMPAIGN_MIN_BUDGET
        : WALMART_SELLER_SINGLE_CAMPAIGN_MIN_BUDGET;
      check(
        planToValidate.dailyBudget < minBudgetRequirement,
        "dailyBudget",
        t(($) => $.advertising["Daily budget must be at least {amount}"], {
          amount: formatCurrency(minBudgetRequirement, {
            marketPlace: marketPlace ?? "USA",
            decimalPlaces: 0,
          }),
        }),
      );

      if ([AD_TYPES.SPONSORED_BRAND_WALMART.value].includes(planToValidate.adType)) {
        check(
          planToValidate?.product?.length < 2 || planToValidate?.product?.length > 10,
          "product",
          t(($) => $.advertising["Please select between 2 and 10 products"]),
        );
        check(
          planToValidate.walmartBrandName?.length < 1 || !planToValidate.walmartBrandName,
          "walmartBrandName",
          t(($) => $.advertising["A brand name is required"]),
        );
        check(
          planToValidate.walmartBrandName?.length > 35,
          "walmartBrandName",
          t(($) => $.advertising["Brand name must be 35 characters in length or less"]),
        );
        check(
          planToValidate.walmartHeadline?.length < 1 || !planToValidate.walmartHeadline,
          "walmartHeadline",
          t(($) => $.advertising["A headline is required"]),
        );
        check(
          planToValidate.walmartHeadline?.length > 45,
          "walmartHeadline",
          t(($) => $.advertising["Headline must be 45 characters in length or less"]),
        );
        check(
          planToValidate.walmartHeadline?.length &&
            // Below triggers validation message on presence of any special chars
            !/^(\w\s?)+$/g.test(planToValidate.walmartHeadline),
          "walmartHeadline",
          t(($) => $.advertising["The headline may not contain any special characters"]),
        );
        check(
          !planToValidate.id &&
            planToValidate.adType === AD_TYPES.SPONSORED_BRAND_WALMART.value &&
            (planToValidate.walmartImage?.length < 1 || !planToValidate.walmartImage),
          "walmartImage",
          t(($) => $.advertising["An image is required"]),
        );
        check(
          planToValidate.walmartClickURL?.length < 1 || !planToValidate.walmartClickURL,
          "walmartClickURL",
          t(($) => $.advertising["A click URL is required"]),
        );
        check(
          planToValidate.walmartClickURL && planToValidate.walmartClickURL?.indexOf("walmart.com") === -1,
          "walmartClickURL",
          t(($) => $.advertising['The click URL must include "walmart.com"']),
        );
      }

      if ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(planToValidate.adType)) {
        check(
          !planToValidate.id &&
            planToValidate.adType === AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value &&
            (planToValidate.walmartVideo?.length < 1 || !planToValidate.walmartVideo),
          "walmartImage",
          t(($) => $.advertising["A video is required"]),
        );
        check(
          planToValidate?.product?.length < 1 || planToValidate?.product?.length > 10,
          "product",
          t(($) => $.advertising["Please select between 1 and 10 products"]),
        );
      }
    }

    check(
      planToValidate.hasParent && planToValidate.allocation === "",
      "allocation",
      t(($) => $.advertising["Daily budget allocation is required"]),
    );
    check(
      !planToValidate.canHaveChildren && planToValidate.targetACOS === "",
      "targetACOS",
      roas ? t(($) => $.advertising["Target ROAS is required"]) : t(($) => $.advertising["Target ACOS is required"]),
    );
    check(
      !planToValidate.canHaveChildren && parseInt(planToValidate.targetACOS) === 0,
      "targetACOS",
      roas ?
        t(($) => $.advertising["Target ROAS must be greater than 0"])
      : t(($) => $.advertising["Target ACOS must be greater than 0"]),
    );
    check(
      !planToValidate.canHaveChildren && planToValidate.maxACOS === "",
      "maxACOS",
      roas ? t(($) => $.advertising["Max ROAS is required"]) : t(($) => $.advertising["Max ACOS is required"]),
    );
    check(
      !planToValidate.canHaveChildren &&
        (roas ?
          parseFloat(planToValidate.targetACOS) <= parseFloat(planToValidate.maxACOS)
        : parseFloat(planToValidate.maxACOS) <= parseFloat(planToValidate.targetACOS)),
      "targetACOS",
      roas ?
        t(($) => $.advertising["ROAS range high must be greater than ROAS range low"])
      : t(($) => $.advertising["ACOS range high must be greater than ACOS range low"]),
    );

    check(
      !planToValidate.canHaveChildren && !planToValidate.adType,
      "adType",
      t(($) => $.advertising["An Ad Type is required"]),
    );
    check(
      !planToValidate.canHaveChildren && !planToValidate.goal,
      "goal",
      t(($) => $.advertising["A Goal is required"]),
    );
    check(
      !planToValidate.canHaveChildren && !planToValidate.targetType,
      "targetType",
      t(($) => $.advertising["A target type is required"]),
    );
    check(
      planToValidate.canHaveChildren && planToValidate.children.length <= 0,
      "children",
      t(($) => $.advertising["Component media plans are required"]),
    );
    check(
      planToValidate.advStatus === AD_STATES.ARCHIVE.value && !agreeToArchive,
      "agreeToArchive",
      t(($) => $.advertising["You must agree before you can save"]),
    );

    if (planToValidate.targetType === TARGET_TYPES.FUNNEL.value) {
      if (!planToValidate.canHaveChildren) {
        check(
          planToValidate.filledProductKeys?.length < 1,
          "filledProductKeys",
          t(($) => $.advertising["At least one product type is required"]),
        );
      }
      // Validate at least one home brand and one competitor brand added for segmented
      if (planToValidate.canHaveChildren && !planToValidate.id) {
        check(
          planToValidate.filledProductBrands?.length < 1,
          "filledProductBrands",
          t(($) => $.advertising["At least one product brand is required"]),
        );
        check(
          planToValidate.filledCompetitorBrands?.length < 1,
          "filledCompetitorBrands",
          t(($) => $.advertising["At least one competitor brand is required"]),
        );
        // Check for collision between home brand and competitor brands
        check(
          planToValidate.filledProductBrands?.some((brand) => planToValidate.filledCompetitorBrands?.includes(brand)),
          "brandTargetCollision",
          t(($) => $.advertising["The same brand was detected in both home and competitor brand fields."]),
        );
      }
    }

    if (
      planToValidate.adType === AD_TYPES.SPONSORED_PRODUCT.value ||
      (planToValidate.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value && !planToValidate.storeLandingPage) ||
      planToValidate.adType === AD_TYPES.SPONSORED_DISPLAY.value ||
      planToValidate.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ||
      planToValidate.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO.value ||
      planToValidate.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
    ) {
      check(
        planToValidate?.product?.length <= 0,
        "product",
        t(($) => $.advertising["Products are required"]),
      );
    }

    if (planToValidate.channel === "AMZ" && [AD_TYPES.SPONSORED_PRODUCT.value].includes(planToValidate.adType)) {
      check(
        isNaN(parseFloat(planToValidate.topOfSearchBidMultiplier)) ||
          planToValidate.topOfSearchBidMultiplier < 0 ||
          planToValidate.topOfSearchBidMultiplier > 900,
        "topOfSearchBidMultiplier",
        t(($) => $.advertising["The top-of-search multiplier must be a value between 0 and 900"]),
      );
      check(
        isNaN(parseFloat(planToValidate.productPageMultiplier)) ||
          planToValidate.productPageMultiplier < 0 ||
          planToValidate.productPageMultiplier > 900,
        "productPageMultiplier",
        t(($) => $.advertising["The product page multiplier must be a value between 0 and 900"]),
      );
      check(
        isNaN(parseFloat(planToValidate.restOfSearchMultiplier)) ||
          planToValidate.restOfSearchMultiplier < 0 ||
          planToValidate.restOfSearchMultiplier > 900,
        "restOfSearchMultiplier",
        t(($) => $.advertising["The rest-of-search multiplier must be a value between 0 and 900"]),
      );
      check(
        isNaN(parseFloat(planToValidate.amazonBusinessMultiplier)) ||
          planToValidate.amazonBusinessMultiplier < 0 ||
          planToValidate.amazonBusinessMultiplier > 900,
        "amazonBusinessMultiplier",
        t(($) => $.advertising["The Amazon business multiplier must be a value between 0 and 900"]),
      );
      check(
        hasAMCModule &&
          (isNaN(parseFloat(planToValidate.audienceBidMultiplier)) ||
            planToValidate.audienceBidMultiplier < 0 ||
            planToValidate.audienceBidMultiplier > 900),
        "audienceBidMultiplier",
        t(($) => $.advertising["The Amazon audience multiplier must be a value between 0 and 900"]),
      );
    }

    if ((planToValidate.channel === "AMZ" && planToValidate.campaignNames) || planToValidate.children) {
      check(
        campaignNameChecks.checking.size,
        "validatingCampaignNames",
        t(($) => $.advertising["Validating campaign name. Please wait."]),
      );
      check(
        campaignNameChecks.failed.size,
        "validatingCampaignNames",
        t(($) => $.advertising["Error validating campaign name."]),
      );
      check(
        campaignNameChecks.taken.size,
        "duplicateCampaignNames",
        t(($) => $.advertising["Duplicate campaign names found. Please enter a different name."]),
      );
      // Below checks that no shared custom campaign names are the same while filtering out empty fields
      check(
        new Set(Object.values(planToValidate?.campaignNames)?.filter((name) => name)).size !==
          Object.values(planToValidate?.campaignNames)?.filter((name) => name)?.length,
        "duplicateCampaignNames",
        t(($) => $.advertising["Duplicate campaign names found. Please enter a different name."]),
      );
      check(
        Object.values(planToValidate.campaignNames)?.some((name) => name?.length > 128),
        "campaignNames",
        t(($) => $.advertising["Campaign names cannot be more than 128 characters in length."]),
      );
      check(
        !adId && Object.values(planToValidate.campaignNames)?.some((name) => /[<>'!%#./}]/.test(name ?? "")),
        "campaignNames",
        t(($) => $.advertising["Campaign names cannot include the following characters: <>'!%#./}"]),
      );

      // Segmented campaign name checks
      if (planToValidate?.canHaveChildren && !planToValidate?.id) {
        check(
          new Set(
            planToValidate?.children
              .map((c) => c.campaignNames)
              .map((c) => Object.values(c ?? {})?.[0])
              ?.filter((name) => name),
          ).size !==
            planToValidate.children
              .map((c) => c.campaignNames)
              .map((c) => Object.values(c ?? {})?.[0])
              ?.filter((name) => name)?.length,
          "duplicateSegmentedCampaignNames",
          t(($) => $.advertising["Campaign names cannot be more than 128 characters in length."]),
        );
        check(
          planToValidate.children
            .map((c) => c.campaignNames)
            .map((c) => Object.values(c ?? {})?.[0])
            ?.some((name) => name?.length > 128),
          "campaignNames",
          t(($) => $.advertising["Campaign names cannot be more than 128 characters in length."]),
        );
      }
    }

    if (planToValidate.adType === AD_TYPES.SPONSORED_BRAND.value && !planToValidate?.page?.label) {
      check(
        !planToValidate.keywordDiscoveryEnabled && planToValidate.filledTags.length <= 0,
        "filledTags",
        t(($) => $.advertising["Select discover keywords or enter your keywords"]),
      );
      check(
        (planToValidate.landingPageProduct?.length ?? 0) < 3 &&
          !adId &&
          !planToValidate.storeSpotlight &&
          !planToValidate.autoCollection &&
          // Manual Collection stocks no landing page of its own; its products are the creative's.
          !planToValidate.manualCollection,
        "landingPageProduct",
        t(($) => $.advertising["Please select at least 3 products for the new landing page"]),
      );
      check(
        planToValidate.landingPageProduct?.length > 100 && !adId,
        "landingPageProduct",
        t(
          ($) =>
            $.advertising[
              "No more than 100 products may be selected for the new landing page. Please remove products."
            ],
          {
            count: planToValidate.landingPageProduct.length - 100,
          },
        ),
      );
    }

    if ([AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(planToValidate.adType)) {
      // Amazon caps the manual collection title at 32 characters, other SB/SD headlines at 50
      if (planToValidate.manualCollection) {
        check(
          planToValidate.headline?.length > 32,
          "headline",
          t(($) => $.advertising["Headlines cannot be more than 32 characters. Please shorten your headline."], {
            count: planToValidate.headline?.length - 32,
          }),
        );
      } else {
        check(
          planToValidate.headline?.length > 50,
          "headline",
          t(($) => $.advertising["Headlines cannot be more than 50 characters. Please shorten your headline."], {
            count: planToValidate.headline?.length - 50,
          }),
        );
      }
    }

    // Lifestyle image and logo creative validations
    if (
      [
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
        AD_TYPES.SPONSORED_DISPLAY_V2.value,
      ]?.includes(planToValidate.adType)
    ) {
      // Logo validations: 1MB or less, 400 x 400 or above for SB, or 600 x 100 or above for SD, PNG, JPEG or JPG
      if (planToValidate.logoDetails) {
        check(
          planToValidate.logoDetails?.file_meta_data?.sizeInBytes > 1000000,
          "logo",
          t(($) => $.advertising["The selected logo must be less than 1MB in size."]),
        );
        check(
          [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value]?.includes(planToValidate.adType) &&
            planToValidate.logoDetails?.file_meta_data?.height < 400,
          "logo",
          t(($) => $.advertising["The selected logo must be at least 400px in height."]),
        );
        check(
          [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value]?.includes(planToValidate.adType) &&
            planToValidate.logoDetails?.file_meta_data?.width < 400,
          "logo",
          t(($) => $.advertising["The selected logo must be at least 400px wide."]),
        );
        check(
          [AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(planToValidate.adType) &&
            planToValidate.logoDetails?.file_meta_data?.height < 100,
          "logo",
          t(($) => $.advertising["The selected logo must be at least 100px in height."]),
        );
        check(
          [AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(planToValidate.adType) &&
            planToValidate.logoDetails?.file_meta_data?.width < 600,
          "logo",
          t(($) => $.advertising["The selected logo must be at least 600px wide."]),
        );
        check(
          !["JPEG", "JPG", "PNG"].includes(planToValidate.logoDetails?.file_meta_data?.extension),
          "logo",
          t(($) => $.advertising["The selected logo must be a PNG or JPEG file."]),
        );
      }
      // Lifestyle image validations: 5MB or less, 1200 x 628 or above, PNG, JPEG or JPG
      if (planToValidate.lifestyleImageDetails) {
        check(
          planToValidate.lifestyleImageDetails?.file_meta_data?.sizeInBytes > 5000000,
          "lifestyleImage",
          t(($) => $.advertising["The selected asset cannot be greater than 5MB in size."]),
        );
        check(
          planToValidate.lifestyleImageDetails?.file_meta_data?.height < 628,
          "lifestyleImage",
          t(($) => $.advertising["The selected image must be at least 628px in height."]),
        );
        check(
          planToValidate.lifestyleImageDetails?.file_meta_data?.width < 1200,
          "lifestyleImage",
          t(($) => $.advertising["The selected image must be at least 1200px wide."]),
        );
        check(
          !["JPEG", "JPG", "PNG"].includes(planToValidate.lifestyleImageDetails?.file_meta_data?.extension),
          "lifestyleImage",
          t(($) => $.advertising["The selected asset must be a PNG or JPEG file."]),
        );
        check(
          [AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(planToValidate.adType) &&
            planToValidate.customSdStorePageEnabled &&
            !planToValidate.page,
          "customSdStorePageEnabled",
          t(($) => $.advertising["A store page is required for the custom landing page"]),
        );
      }
    }

    if (planToValidate.adType === AD_TYPES.SPONSORED_BRAND.value) {
      const isCollection = planToValidate.manualCollection || planToValidate.autoCollection;
      check(
        !isCollection && planToValidate.headline?.length <= 1,
        "headline",
        t(($) => $.advertising["Headline is required"]),
      );

      check(
        !isCollection && (planToValidate.logo === "" || !planToValidate.logo) && !planToValidate.logoDetails,
        "logo",
        t(($) => $.advertising["Logo is required"]),
      );
      check(
        !planToValidate.newLandingPage &&
          !planToValidate.storeLandingPage &&
          !planToValidate.newProductCollection &&
          !planToValidate.manualCollection &&
          !planToValidate.autoCollection &&
          !planToValidate.storeSpotlight,
        "landingPage",
        t(($) => $.advertising["Landing page type is required"]),
      );
      if (!(organization_type === "vendor" && brandOptions.length === 0)) {
        check(
          planToValidate.brand === "",
          "brand",
          t(($) => $.advertising["Brand is required"]),
        );
      }
      check(
        planToValidate.creativeBrandName && planToValidate.creativeBrandName.length > 30,
        "creativeBrandName",
        t(($) => $.advertising["The brand name for display in the creative must be 30 or less characters long"]),
      );
      check(
        (planToValidate.newProductCollection ||
          planToValidate.manualCollection ||
          planToValidate.autoCollection ||
          planToValidate.storeLandingPage) &&
          !planToValidate.creativeBrandName,
        "creativeBrandName",
        t(($) => $.advertising["A creative brand name is required"]),
      );
      if (planToValidate.autoCollection) {
        check(
          planToValidate.excludedProducts?.length > 1000,
          "excludedProducts",
          t(($) => $.advertising["You can exclude up to 1000 products. Remove products"], {
            count: planToValidate.excludedProducts?.length - 1000,
          }),
        );
      }
      if (planToValidate.manualCollection) {
        check(
          (planToValidate.creativeProduct?.length < 3 || planToValidate.creativeProduct?.length > 10) && !adId,
          "creativeProduct",
          planToValidate.creativeProduct?.length > 10 ?
            t(($) => $.advertising["Please select 3 to 10 products. Remove products"], {
              count: planToValidate.creativeProduct?.length - 10,
            })
          : t(($) => $.advertising["Please select 3 to 10 products."]),
        );
      } else if (
        !planToValidate.autoCollection &&
        (planToValidate.storeLandingPage || planToValidate.newLandingPage || planToValidate.newProductCollection)
      ) {
        check(
          (planToValidate.creativeProduct?.length < 1 || planToValidate.creativeProduct?.length > 3) && !adId,
          "creativeProduct",
          planToValidate.creativeProduct?.length > 3 ?
            t(($) => $.advertising["Please select 1 to 3 products. Remove products"], {
              count: planToValidate.creativeProduct?.length - 3,
            })
          : t(($) => $.advertising["Please select 1 to 3 products."]),
        );
      }
      check(
        planToValidate.homePlacementBidMultiplier < -99 || planToValidate.homePlacementBidMultiplier > 900,
        "homePlacementBidMultiplier",
        t(($) => $.advertising["Placement multipliers must be a value between -99 and 900"]),
      );
      check(
        planToValidate.pagePlacementBidMultiplier < -99 || planToValidate.pagePlacementBidMultiplier > 900,
        "pagePlacementBidMultiplier",
        t(($) => $.advertising["Placement multipliers must be a value between -99 and 900"]),
      );
      check(
        planToValidate.otherPlacementBidMultiplier < -99 || planToValidate.otherPlacementBidMultiplier > 900,
        "otherPlacementBidMultiplier",
        t(($) => $.advertising["Placement multipliers must be a value between -99 and 900"]),
      );
      check(
        planToValidate.topOfSearchPlacementBidMultiplier < -99 ||
          planToValidate.topOfSearchPlacementBidMultiplier > 900,
        "topOfSearchPlacementBidMultiplier",
        t(($) => $.advertising["Placement multipliers must be a value between -99 and 900"]),
      );
      check(
        planToValidate.shopperSegmentBidMultiplier < 0 || planToValidate.shopperSegmentBidMultiplier > 900,
        "shopperSegmentBidMultiplier",
        t(($) => $.advertising["The shopper segment multiplier must be a value between 0 and 900"]),
      );

      if (planToValidate.storeSpotlight) {
        check(
          !planToValidate.store?.label,
          "store",
          t(($) => $.advertising["A store is required"]),
        );

        check(
          planToValidate.storeSpotlightSubpages?.filter((subpage) => !subpage?.pageTitle)?.length,
          "storeSpotlightSubpages",
          t(($) => $.advertising["A page is required for all 3 store subpages"]),
        );
        check(
          !planToValidate.storeSpotlightSubpages?.filter((subpage) => !subpage?.pageTitle)?.length &&
            planToValidate.storeSpotlightSubpages?.filter((subpage) => !subpage?.asin)?.length,
          "storeSpotlightSubpages",
          t(($) => $.advertising["An ASIN is required for all 3 store subpages"]),
        );
      }
    }

    if (
      [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(planToValidate.adType) &&
      !planToValidate.id &&
      !adId &&
      !planToValidate.storeSpotlight &&
      !planToValidate.autoCollection
    ) {
      const asinLookup = new Map(asinsByProductId);
      for (const storePageProduct of storePageProductsQuery.data ?? []) {
        asinLookup.set(String(storePageProduct.id), storePageProduct.asin);
      }
      const creativeAsins = (planToValidate.creativeProduct ?? [])
        .map((productId) => asinLookup.get(String(productId)))
        .filter(Boolean);
      const duplicatedAsins = [...new Set(creativeAsins.filter((asin, index) => creativeAsins.indexOf(asin) < index))];
      check(
        duplicatedAsins.length > 0,
        "creativeProduct",
        `${t(($) => $.advertising["You have selected duplicate asins to be displayed in the Creative."])} ${t(
          ($) => $.advertising["Remove all duplicates of: {asins}"],
          { asins: duplicatedAsins.join(", ") },
        )}`,
      );
    }

    if (planToValidate.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
      if (!(organization_type === "vendor" && brandOptions.length === 0)) {
        check(
          planToValidate.brand === "",
          "brand",
          t(($) => $.advertising["Brand is required"]),
        );
      }
      check(
        planToValidate.creativeBrandName && planToValidate.creativeBrandName.length > 30,
        "creativeBrandName",
        t(($) => $.advertising["The brand name for display in the creative must be 30 or less characters long"]),
      );
      check(
        planToValidate.videoAdType === "brandVideo" && !planToValidate.store?.label && !planToValidate.id,
        "store",
        t(($) => $.advertising["A store is required"]),
      );
      check(
        planToValidate.videoAdType === "brandVideo" && !planToValidate.page?.label && !planToValidate.id,
        "page",
        t(($) => $.advertising["A store page is required"]),
      );
      check(
        !planToValidate.video && !planToValidate.videoDetails,
        "video",
        t(($) => $.advertising["Video is required"]),
      );
      if (planToValidate.storeLandingPage) {
        check(
          (planToValidate.logo === "" || !planToValidate.logo) && !planToValidate.logoDetails,
          "logo",
          t(($) => $.advertising["Logo is required"]),
        );
        check(
          planToValidate.headline?.length <= 1,
          "headline",
          t(($) => $.advertising["Headline is required"]),
        );
      }
      check(
        planToValidate.product.length > 1,
        "product",
        t(($) => $.advertising["You have exceed the maximum number of products for a video. Remove products."], {
          count: planToValidate.product.length - 1,
        }),
      );
      if (planToValidate.newLandingPage) {
        check(
          planToValidate.creativeProduct.length > 1,
          "product",
          t(
            ($) =>
              $.advertising["You have exceed the maximum number of products for a video creative. Remove products."],
            {
              count: planToValidate.product.length - 1,
            },
          ),
        );
      }
      if (planToValidate.storeLandingPage) {
        check(
          planToValidate.creativeProduct.length <= 0 && planToValidate.product.length <= 0,
          "product",
          t(($) => $.advertising["A product is required"]),
        );
      } else {
        check(
          planToValidate.product.length <= 0,
          "product",
          t(($) => $.advertising["A product is required"]),
        );
      }
      // Video validations: duration 6-45s, max size 50MB
      // Don't re-validate after creation to avoid issues with legacy videos
      if (planToValidate.videoDetails && !planToValidate.id) {
        check(
          planToValidate.videoDetails?.file_meta_data?.sizeInBytes > 500000000,
          "video",
          t(($) => $.advertising["The selected video cannot be greater than 500MB in size."]),
        );
        check(
          planToValidate.videoDetails?.file_meta_data?.durationInSeconds < 6,
          "video",
          t(($) => $.advertising["The selected video must be at least 6s in duration."]),
        );
        check(
          planToValidate.videoDetails?.file_meta_data?.durationInSeconds > 45,
          "video",
          t(($) => $.advertising["The selected video cannot be more than 45s in duration."]),
        );
      }
    }

    if (
      [
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
        AD_TYPES.SPONSORED_DISPLAY_V2.value,
      ].includes(planToValidate.adType) &&
      planToValidate.costControlTargetCPCEnabled
    ) {
      check(
        !planToValidate.costControlTargetCPC || planToValidate.costControlTargetCPC < MIN_SB_TARGET_CPC,
        "costControlTargetCPC",
        t(($) => $.advertising["The target CPC must be at least {amount}."], {
          amount: formatCurrency(MIN_SB_TARGET_CPC, { marketPlace: marketPlace.marketPlace }),
        }),
      );
    }

    // TODO: Verify validation for manual targeting fields
    if (
      planToValidate.targetType === TARGET_TYPES.MANUAL.value &&
      !planToValidate.hasParent &&
      planToValidate.adType !== AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value
    ) {
      check(
        planToValidate.filledTags?.length === 0 &&
          planToValidate.filledAsins?.length === 0 &&
          planToValidate.filledTargetCategories?.length === 0,
        "filledTagsOrAsins",
        planToValidate.channel === "walmart" ?
          t(($) => $.advertising["Keywords are required"])
        : t(($) => $.advertising["Keywords, categories or ASINs are required"]),
      );

      if (planToValidate.filledAsins?.length > 0) {
        check(
          planToValidate.filledAsins?.map((x) => !/^[a-zA-Z0-9]{10}$/.test(x)).find((r) => r),
          "filledAsins",
          t(($) => $.advertising["Invalid ASIN"]),
        );
      }
    }
    if (planToValidate.targetType === TARGET_TYPES.MANUAL.value && !planToValidate.hasParent) {
      if (planToValidate.filledNegativeAsins?.length > 0) {
        check(
          planToValidate.filledNegativeAsins?.map((x) => !x.startsWith("B0")).find((r) => r),
          "filledNegativeAsins",
          t(($) => $.advertising["Invalid negative ASIN"]),
        );
      }
    }

    const tagError = getFirstKeywordError(planToValidate.filledTags, {
      allowLeadingPlus: planToValidate.channel === "AMZ",
    });
    check(!!tagError, "filledTags", tagError && keywordErrorMessage(tagError.code));

    const negativeKeywordError = getFirstKeywordError(planToValidate.filledNegativeKeywords);
    check(
      !!negativeKeywordError,
      "filledNegativeKeywords",
      negativeKeywordError && keywordErrorMessage(negativeKeywordError.code),
    );

    if (planToValidate.hasParent && planToValidate.id) {
      check(
        (planToValidate?.parentDailyBudget / 100.0) * parseFloat(planToValidate?.allocation) < 1,
        "allocation",
        t(($) => $.advertising["A minimum daily budget of {amount} is required."], {
          amount: formatCurrency(1, {
            marketPlace: marketPlace.marketPlace,
          }),
        }),
      );
    }

    // SD V2 Validations
    if (planToValidate.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
      check(
        !planToValidate.sdBidType,
        "sdBidType",
        t(($) => $.advertising["Please select a bid type for this campaign."]),
      );

      check(
        !planToValidate.sdCampaignType,
        "sdCampaignType",
        t(($) => $.advertising["Please select a campaign type for this campaign."]),
      );
      check(
        planToValidate.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
          !planToValidate.lookbackPeriod.length &&
          !planToValidate.filledTargetAudiences?.length,
        "sdLookbackPeriod",
        t(($) => $.advertising["Please select at least one lookback period option."]),
      );
      check(
        planToValidate.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value &&
          !planToValidate.viewsSimilar &&
          !planToValidate.viewsExact &&
          !planToValidate.purchasesSimilar &&
          !planToValidate.purchasesExact &&
          !planToValidate.sdAmazonSuggestedCategories &&
          !planToValidate.filledTargetCategories?.length &&
          !planToValidate.filledTargetAudiences?.length,
        "sdAudienceTargets",
        t(($) => $.advertising["Please select at least one audience targeting option."]),
      );
      check(
        planToValidate.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL.value &&
          !planToValidate.sdDynamicSimilar &&
          !planToValidate.sdAmazonSuggestedCategories &&
          !planToValidate.filledAsins?.length &&
          !planToValidate.filledTargetCategories?.length,
        "sdContextualTargets",
        t(($) => $.advertising["Please select at least one contextual targeting option."]),
      );
    }
    // Validations for custom bidding programs - only check if program exists or is being created, all other validations happen internally within rules form components
    check(
      planToValidate.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value &&
        !planToValidate.biddingProgram &&
        !planToValidate.createNewProgram,
      "existingBiddingProgram",
      t(($) => $.advertising["A bidding program is required. Please select an existing program or create a new one."]),
    );
    check(
      planToValidate.algorithm === ALGORITHM_OPTIONS.RULES_ENGINE_ONLY.value &&
        !planToValidate.biddingProgram &&
        planToValidate.createNewProgram,
      "newBiddingProgram",
      t(($) => $.advertising["A bidding program is required. Please select an existing program or create a new one."]),
    );

    return { errors, isValid };
  };

  // Recomputed every render rather than memoized: about a millisecond (mostly building messages),
  // small next to the form re-render it comes with, and nothing downstream needs a stable object.
  const { errors, isValid } = validate();

  const updateChild = (child, idx = null) => {
    if (idx == null) {
      idx = adPlan.children.length;
    }
    const children = [...adPlan.children.slice(0, idx), child, ...adPlan.children.slice(idx + 1)];
    onChange({ children });
  };

  const removeChild = (idx) => {
    onChange({ children: adPlan.children.filter((c, i) => i !== idx) });
  };

  const clear = () => {
    setAdPlan(initialAdPlan);
  };

  return {
    adPlan,
    isValid,
    errors,
    onChange,
    clear,
    updateChild,
    removeChild,
    brandOptions,
    savedVideos: savedVideosQuery.data ?? [],
    logoOptions,
    storeOptions,
    pageOptions,
    loading:
      isBrandDataLoading ||
      adPlanConfigQuery.isLoading ||
      storeDataQuery.isLoading ||
      savedVideosQuery.isLoading ||
      lifestyleAssetDetailsQuery.isLoading ||
      logoAssetDetailsQuery.isLoading,
    agreeToArchive,
    setAgreeToArchive,
  };
};

export default useAdPlanState;
